/**
 * CloudSettingsPage.tsx - Cloud gateway configuration (website_api .env)
 */

import { useCallback, useEffect, useState } from "react";
import {
  getAdminConfig,
  getHealthStatus,
  updateAdminConfig,
  type AdminConfig,
  type HealthStatus,
} from "../api/adminClient";

const inputClass =
  "w-full px-4 py-2.5 border border-[var(--color-outline-variant)] rounded-lg text-sm bg-[var(--color-surface)] text-[var(--color-on-surface)] placeholder:text-[var(--color-outline)] focus:outline-none focus:border-[var(--color-accent-blue)] focus:ring-1 focus:ring-[var(--color-accent-blue)]";

const SECTIONS: { title: string; keys: string[]; hints?: Record<string, string> }[] = [
  {
    title: "推理 / Embedding",
    keys: [
      "CLIP_MODEL",
      "EMBED_DIMENSION",
      "GOOGLE_API_KEY",
      "GOOGLE_IMAGE_API_KEY",
      "GOOGLE_TEXT_API_KEY",
      "GOOGLE_EMBED_MODEL",
      "DASHSCOPE_API_KEY",
    ],
  },
  {
    title: "OCR (Paddle)",
    keys: [
      "PADDLE_API_TOKEN",
      "PADDLE_OCR_MODE",
      "PADDLE_API_URL",
      "PADDLE_SYNC_API_URL",
      "PADDLE_OCR_MODEL",
      "PADDLE_POLL_INTERVAL",
      "PADDLE_OCR_TIMEOUT",
    ],
  },
  {
    title: "计费",
    keys: [
      "BILLING_FREE_QUOTA_CNY",
      "BILLING_MIN_BALANCE_CNY",
      "DAILY_FREE_TEXT_EMBEDS",
      "MEMBER_VECTORIZE_FREE_THRESHOLD_CNY",
    ],
  },
  {
    title: "内容缓存",
    keys: [
      "CONTENT_CACHE_ENABLED",
      "CONTENT_CACHE_STORE_RAW",
      "CONTENT_CACHE_MAX_BYTES",
      "CONTENT_CACHE_TTL_DAYS",
    ],
  },
  {
    title: "邮件 (Resend)",
    keys: ["RESEND_API_KEY", "EMAIL_FROM"],
    hints: {
      RESEND_API_KEY: "Resend 控制台 API Key",
      EMAIL_FROM: "发件人，需已验证域名，如 MT Copilot <noreply@mtcopilot.xyz>",
    },
  },
  {
    title: "邮件 (SMTP 备用)",
    keys: ["SMTP_HOST", "SMTP_PORT", "SMTP_USER", "SMTP_PASSWORD", "SMTP_FROM"],
  },

  {
    title: "系统 / 网关",
    keys: [
      "WEBSITE_CORS_ORIGINS",
      "WEBSITE_OCR_ENDPOINT_TIMEOUT",
      "WEBSITE_EMBED_ENDPOINT_TIMEOUT",
      "WEBSITE_API_DEV_MODE",
      "ADMIN_DEFAULT_USERNAME",
    ],
  },
];

function isSecretKey(key: string): boolean {
  return /KEY|SECRET|PASSWORD|TOKEN/i.test(key);
}

function ConfigField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  const [visible, setVisible] = useState(false);
  const secret = isSecretKey(label);
  return (
    <div className="mb-4">
      <label className="block text-xs font-medium text-[var(--color-on-surface-variant)] mb-1.5">
        {label}
      </label>
      <div className="relative">
        <input
          type={secret && !visible ? "password" : "text"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={`${inputClass}${secret ? " pr-16" : ""}`}
        />
        {secret && (
          <button
            type="button"
            onClick={() => setVisible((v) => !v)}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-[var(--color-on-surface-variant)]"
          >
            {visible ? "隐藏" : "显示"}
          </button>
        )}
      </div>
    </div>
  );
}

interface CloudSettingsPageProps {
  onAuthError?: () => void;
}

export default function CloudSettingsPage({ onAuthError }: CloudSettingsPageProps) {
  const [config, setConfig] = useState<AdminConfig>({});
  const [local, setLocal] = useState<AdminConfig>({});
  const [health, setHealth] = useState<HealthStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setMessage(null);
    try {
      const cfg = await getAdminConfig();
      const filtered = Object.fromEntries(
        Object.entries(cfg).filter(([k]) => !k.endsWith("_configured")),
      );
      setConfig(filtered);
      setLocal(filtered);
      try {
        const h = await getHealthStatus();
        setHealth(h);
      } catch (e: unknown) {
        setHealth(null);
        const msg = e instanceof Error ? e.message : String(e);
        setMessage(`配置已加载，健康检查失败：${msg}`);
      }
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(msg);
      if (msg.includes('401') || msg.includes('403')) {
        onAuthError?.();
      }
    } finally {
      setLoading(false);
    }
  }, [onAuthError]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    setError(null);
    try {
      const merged = { ...config, ...local };
      const result = await updateAdminConfig(merged);
      setMessage(result.message || "已保存");
      await load();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="text-[var(--color-on-surface-variant)] py-12 text-center">
        加载云端配置...
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-[var(--color-on-surface)]">云端配置</h2>
          <p className="text-sm text-[var(--color-on-surface-variant)] mt-1">
            写入 website_api/.env；JWT 密钥需手动编辑服务端文件
          </p>
        </div>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="px-4 py-2.5 rounded-lg text-sm font-medium bg-[var(--color-secondary-container)] text-white disabled:opacity-50"
        >
          {saving ? "保存中..." : "保存全部"}
        </button>
      </div>

      {health?.config && (
        <div className="mb-6 p-4 rounded-xl bg-[var(--color-surface-container-low)] text-sm flex flex-wrap gap-3">
          <span>JWT: {health.config.jwt_configured ? "已配置" : "未配置"}</span>
          <span>Paddle: {health.config.paddle_configured ? "已配置" : "未配置"}</span>
          <span>Google: {health.config.google_configured ? "已配置" : "未配置"}</span>
          <span>百炼: {health.config.dashscope_configured ? "已配置" : "未配置"}</span>
          <span>邮件: {health.config.email_configured ? "已配置" : "未配置"}</span>
        </div>
      )}

      {message && (
        <div className="mb-4 p-3 rounded-lg bg-[var(--color-success-container)] text-[var(--color-on-success-container)] text-sm">
          {message}
        </div>
      )}
      {error && (
        <div className="mb-4 p-3 rounded-lg bg-[var(--color-error-container)] text-[var(--color-on-error-container)] text-sm">
          {error}
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        {SECTIONS.map((section) => (
          <div
            key={section.title}
            className="bg-[var(--color-surface)] rounded-2xl p-6 border border-[var(--color-border-subtle)]"
          >
            <h3 className="text-base font-semibold text-[var(--color-on-surface)] mb-4">
              {section.title}
            </h3>
            {section.keys.map((key) => (
              <ConfigField
                key={key}
                label={key}
                value={local[key] ?? ""}
                onChange={(v) => setLocal((prev) => ({ ...prev, [key]: v }))}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
